import { useEffect, useMemo, useRef, useState } from 'react'
import { usePostIdsHealthcareservicesHcsIdWorkflowsWorkflowIdComputedValuesStepId } from '@toktokdoctech/tokapi-client'
import useCurrentHcs from '../../../../../hooks/useCurrentHcs'
import { useAppDispatch } from '../../../../../store'
import { displaySnackbar } from '../../../../../actions/main'
import {
    getWorkflowProgress,
    getFirstQuestionId,
    buildSavedValuesForFormula,
    parseTransmissionRequest,
} from '../../../../utilsComponents/workflowParser'
import {
    getAnsweredPath,
    getPreviousVisibleQuestionId,
    resolveAutoNextQuestionId,
    resolveNextQuestionId,
} from '../utils/workflowQuestionHelpers'
import { persistCurrentQuestion, getPersistedQuestion, clearPersistedProgress } from '../utils/workflowStorage'
import { ParsedAnswer } from '../../../questionTypes/_props'
import { WorkflowFormQuestion } from '../../../../utilsComponents/workflowParser'
import { validateNumber } from '../../../../utils/number'
import { validateDateInRange } from '../../../../utils/date'
import { RECAP_POSITION } from '../constants'

type SaveProgressFn = (answersMap: Map<string, any[]>, positionId: string | null) => Promise<void>
type SendTransmissionFn = (committedMap: Map<string, any[]>) => Promise<void>

export function useWorkflowNavigation(
    workflowQuestions: WorkflowFormQuestion[],
    rawByIdMap: Map<string, any>,
    workflowJsonContent: any,
    transmissionDetail: any,
    isTransmissionDetailFetching: boolean,
    transmissionId: string | undefined,
    requestAnswers: ParsedAnswer[],
    saveProgress: SaveProgressFn,
    sendTransmission: SendTransmissionFn,
) {
    const currentHcs = useCurrentHcs()
    const dispatch = useAppDispatch()
    const { mutateAsync: evaluateComputedValue } =
        usePostIdsHealthcareservicesHcsIdWorkflowsWorkflowIdComputedValuesStepId()
    const [localAnswersByQuestionId, setLocalAnswersByQuestionId] = useState<Map<string, any[]>>(new Map())
    const [committedAnswersByQuestionId, setCommittedAnswersByQuestionId] = useState<Map<string, any[]>>(new Map())
    const [currentQuestionId, setCurrentQuestionId] = useState<string | null>(null)
    const [visitedQuestionIds, setVisitedQuestionIds] = useState<string[]>([])
    const [isRecap, setIsRecap] = useState(false)
    const [isNavigating, setIsNavigating] = useState(false)
    const isInitializedRef = useRef(false)
    const isNavigatingRef = useRef(false)
    const pendingComputedIdsRef = useRef<Set<string>>(new Set())

    const startNavigation = () => {
        isNavigatingRef.current = true
        setIsNavigating(true)
    }

    const endNavigation = () => {
        isNavigatingRef.current = false
        setIsNavigating(false)
    }

    // same rule as useQuestionTypeFlags / PlannedExpertiseWorkflowContent
    const isSingleChoice = (question?: WorkflowFormQuestion) =>
        Boolean(question?.choices?.length) &&
        !question?.multiple?.trigger &&
        (!question?.inputType || question.inputType === 'choice')

    /**
     * prepares the local (editable) answer of the question being displayed.
     * - back on a single choice question: the selection is reset, nothing is preselected
     * - otherwise: the committed answer is restored when nothing is being edited
     */
    const enterQuestion = (questionId: string, direction: 'back' | 'forward', committedMap: Map<string, any[]>) => {
        const question = workflowQuestions.find((q) => q.id === questionId)
        setLocalAnswersByQuestionId((previous) => {
            if (direction === 'back' && isSingleChoice(question)) {
                return new Map(previous).set(questionId, [])
            }
            const committedAnswers = committedMap.get(questionId) ?? []
            const localAnswers = previous.get(questionId) ?? []
            if (localAnswers.length > 0 || committedAnswers.length === 0) return previous
            return new Map(previous).set(questionId, committedAnswers)
        })
    }

    /**
     * Initialization: restores previously submitted answers and the current position.
     * Priority order:
     * 1. API (source of truth) → `__currentQuestionId__` field
     * 2. sessionStorage (cached fallback)
     * 3. Automatic calculation (first unanswered question)
     */
    useEffect(() => {
        if (isInitializedRef.current) return
        if (isTransmissionDetailFetching) return
        if (!transmissionDetail?.request) return
        if (workflowQuestions.length === 0) return

        const {
            answersByQuestionId: initialMap,
            skippedQuestionIds: apiSkippedQuestionIds,
            currentQuestionId: apiSavedPosition,
        } = parseTransmissionRequest(transmissionDetail.request, transmissionDetail)
        // a skipped question is committed with an empty answer
        apiSkippedQuestionIds.forEach((questionId) => {
            if (!initialMap.has(questionId)) initialMap.set(questionId, [])
        })
        setCommittedAnswersByQuestionId(initialMap)

        const answeredPath = getAnsweredPath(workflowQuestions, initialMap, workflowJsonContent, rawByIdMap)
        const cachedPosition = transmissionId ? getPersistedQuestion(transmissionId) : null

        if ((apiSavedPosition ?? cachedPosition) === RECAP_POSITION && answeredPath.length > 0) {
            setCurrentQuestionId(answeredPath[answeredPath.length - 1])
            setVisitedQuestionIds(answeredPath)
            setIsRecap(true)
            if (transmissionId) {
                persistCurrentQuestion(transmissionId, RECAP_POSITION)
            }
            isInitializedRef.current = true
            return
        }

        let targetQuestionId: string | null = null
        if (apiSavedPosition && workflowQuestions.some((q) => q.id === apiSavedPosition)) {
            targetQuestionId = apiSavedPosition
            if (transmissionId) {
                persistCurrentQuestion(transmissionId, apiSavedPosition)
            }
        }
        if (!targetQuestionId && cachedPosition && workflowQuestions.some((q) => q.id === cachedPosition)) {
            targetQuestionId = cachedPosition
        }
        if (!targetQuestionId) {
            targetQuestionId =
                answeredPath[answeredPath.length - 1] ?? getFirstQuestionId(workflowQuestions, workflowJsonContent)
        }
        if (targetQuestionId) {
            const targetIdxInPath = answeredPath.indexOf(targetQuestionId)
            enterQuestion(targetQuestionId, 'forward', initialMap)
            setCurrentQuestionId(targetQuestionId)
            setVisitedQuestionIds(
                targetIdxInPath === -1 ? [targetQuestionId] : answeredPath.slice(0, targetIdxInPath + 1),
            )
            if (transmissionId) {
                persistCurrentQuestion(transmissionId, targetQuestionId)
            }
        }
        isInitializedRef.current = true
    }, [
        transmissionDetail,
        isTransmissionDetailFetching,
        workflowQuestions,
        requestAnswers,
        workflowJsonContent,
        rawByIdMap,
        transmissionId,
    ])

    const currentQuestion = currentQuestionId ? workflowQuestions.find((q) => q.id === currentQuestionId) ?? null : null

    /**
     * "back" button availability.
     * - hidden on the first question of the workflow and on the recap
     * - disabled on the first question of a section
     */
    const previousQuestionId = useMemo(() => {
        if (!currentQuestionId) return null
        const path = getAnsweredPath(workflowQuestions, committedAnswersByQuestionId, workflowJsonContent, rawByIdMap)
        return getPreviousVisibleQuestionId(currentQuestionId, path, workflowQuestions)
    }, [currentQuestionId, workflowQuestions, committedAnswersByQuestionId, workflowJsonContent, rawByIdMap])
    const previousQuestion = previousQuestionId ? workflowQuestions.find((q) => q.id === previousQuestionId) : undefined
    const showBack = !isRecap && Boolean(currentQuestion) && Boolean(previousQuestion)
    const canGoBack = showBack && previousQuestion?.sectionLabel === currentQuestion?.sectionLabel

    const progress = getWorkflowProgress(
        workflowQuestions,
        requestAnswers,
        committedAnswersByQuestionId,
        localAnswersByQuestionId,
        currentQuestionId,
    )

    const recapHasErrors = visitedQuestionIds.some((questionId) => {
        const question = workflowQuestions.find((item) => item.id === questionId)
        if (!question) return false

        const value = committedAnswersByQuestionId.get(questionId)?.[0]
        const isEmpty = value == null || value === '' || (Array.isArray(value) && value.length === 0)
        if (isEmpty) return question.required === true

        if (question.inputType === 'text') return typeof value !== 'string' || value.trim().length === 0
        if (question.inputType === 'number') {
            return !validateNumber(String(value), question.numberExpectedFormat, question.numberMin, question.numberMax)
                .isValid
        }
        if (question.inputType === 'date') {
            return !validateDateInRange(String(value), question.dateMin, question.dateMax).isValid
        }
        if (question.inputType === 'slider') {
            const numericValue = Number(value)
            return (
                !Number.isFinite(numericValue) ||
                numericValue < Number(question.sliderMin) ||
                numericValue > Number(question.sliderMax)
            )
        }

        return false
    })

    // safe calculation from tokapi
    const fetchComputedValue = async (questionId: string): Promise<string | undefined> => {
        const workflowId = transmissionDetail?.workflow?.id
        if (!workflowId) return undefined

        const savedValues = buildSavedValuesForFormula(
            workflowQuestions,
            requestAnswers,
            committedAnswersByQuestionId,
            localAnswersByQuestionId,
            questionId,
            transmissionDetail?.patient ?? null,
        )
        const stepId = workflowQuestions.find((q) => q.id === questionId)?.answerId ?? questionId
        try {
            const response = await evaluateComputedValue({
                hcsId: currentHcs.id,
                workflowId,
                stepId,
                data: { savedValues },
            })
            return response?.value ?? undefined
        } catch (error) {
            console.error('Erreur lors du calcul de la valeur automatique:', error)
            return undefined
        }
    }

    /**
     * Automatically computes questions of type "calculated".
     * These questions display a computed value, and the user must click
     * "Next" to continue.
     */
    useEffect(() => {
        if (currentQuestion?.inputType !== 'calculated' || !currentQuestion.id) return

        const questionId = currentQuestion.id
        const alreadyComputed = (localAnswersByQuestionId.get(questionId)?.length ?? 0) > 0
        if (alreadyComputed || pendingComputedIdsRef.current.has(questionId)) return

        const computeCurrentQuestion = async () => {
            pendingComputedIdsRef.current.add(questionId)
            try {
                const result = await fetchComputedValue(questionId)
                if (result === undefined) return
                setLocalAnswersByQuestionId((prev) => new Map(prev).set(questionId, [result]))
            } catch (error) {
                console.error('Erreur lors du calcul de la valeur automatique:', error)
            } finally {
                pendingComputedIdsRef.current.delete(questionId)
            }
        }
        computeCurrentQuestion()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentQuestion,
        requestAnswers,
        committedAnswersByQuestionId,
        localAnswersByQuestionId,
        workflowQuestions,
        transmissionDetail,
    ])

    const navigateToNextQuestion = (nextCommittedMap: Map<string, any[]>, nextQuestionId: string) => {
        enterQuestion(nextQuestionId, 'forward', nextCommittedMap)
        setCurrentQuestionId(nextQuestionId)
        setVisitedQuestionIds((previous) =>
            previous.includes(nextQuestionId) ? previous : [...previous, nextQuestionId],
        )
        if (transmissionId) {
            persistCurrentQuestion(transmissionId, nextQuestionId)
        }
    }

    const processAutoNode = async (
        questionId: string,
        committedMap: Map<string, any[]>,
    ): Promise<Map<string, any[]>> => {
        const question = workflowQuestions.find((q) => q.id === questionId)
        if (!question?.auto) return committedMap

        const updatedMap = new Map(committedMap)
        if (question.inputType === 'calculated') {
            const result = await fetchComputedValue(questionId)
            if (result !== undefined && question.saveValue) {
                updatedMap.set(questionId, [result])
            }
        }
        const rawNode = rawByIdMap.get(questionId)
        if (rawNode?.auto === true && rawNode?.value !== undefined && question.saveValue) {
            updatedMap.set(questionId, [rawNode.value])
        }
        return updatedMap
    }

    // an empty answer means the question is skipped
    const commitAndNavigate = async (questionId: string, answers: any[]) => {
        const nextId = resolveNextQuestionId(questionId, answers, workflowQuestions, workflowJsonContent, rawByIdMap)

        // Commit the answer
        const nextCommittedMap = new Map(committedAnswersByQuestionId)
        nextCommittedMap.set(questionId, answers)

        const previousAnswers = committedAnswersByQuestionId.get(questionId)
        const purgedQuestionIds = new Set<string>()
        const staleCalculatedIds = new Set<string>()
        if (previousAnswers && JSON.stringify(previousAnswers) !== JSON.stringify(answers)) {
            const previousPath = getAnsweredPath(
                workflowQuestions,
                committedAnswersByQuestionId,
                workflowJsonContent,
                rawByIdMap,
            )
            const currentIdxInPath = previousPath.indexOf(questionId)
            const downstreamIds = currentIdxInPath === -1 ? [] : previousPath.slice(currentIdxInPath + 1)
            const previousNextId = resolveNextQuestionId(
                questionId,
                previousAnswers,
                workflowQuestions,
                workflowJsonContent,
                rawByIdMap,
            )
            if (previousNextId !== nextId) {
                downstreamIds.forEach((id) => purgedQuestionIds.add(id))
            } else {
                downstreamIds
                    .filter((id) => workflowQuestions.find((q) => q.id === id)?.inputType === 'calculated')
                    .forEach((id) => staleCalculatedIds.add(id))
            }
        }
        purgedQuestionIds.forEach((id) => nextCommittedMap.delete(id))
        // the transmission is saved first: on failure the user stays on the current question
        startNavigation()
        try {
            await saveProgress(nextCommittedMap, nextId ?? RECAP_POSITION)
        } catch (error) {
            console.error('Erreur de sauvegarde:', error)
            dispatch(
                displaySnackbar(
                    'error',
                    'Une erreur a empêché le passage à l’étape suivante, veuillez recommencer.',
                    undefined,
                    '[useWorkflowNavigation] save progress failed',
                ),
            )
            endNavigation()
            return
        }

        setCommittedAnswersByQuestionId(nextCommittedMap)
        setLocalAnswersByQuestionId((previous) => {
            const next = new Map(previous)
            next.set(questionId, answers)
            purgedQuestionIds.forEach((id) => next.delete(id))
            staleCalculatedIds.forEach((id) => next.delete(id))
            return next
        })
        setVisitedQuestionIds((previous) => {
            const kept = previous.filter((id) => !purgedQuestionIds.has(id))
            return kept.includes(questionId) ? kept : [...kept, questionId]
        })

        if (!nextId) {
            setIsRecap(true)
            if (transmissionId) {
                persistCurrentQuestion(transmissionId, RECAP_POSITION)
            }
        } else {
            navigateToNextQuestion(nextCommittedMap, nextId)
        }
        endNavigation()
    }

    const handleNext = () => {
        if (!currentQuestion?.id) return
        if (isNavigatingRef.current) return

        const currentAnswers =
            localAnswersByQuestionId.get(currentQuestion.id) ??
            committedAnswersByQuestionId.get(currentQuestion.id) ??
            []
        if (currentQuestion.required && currentAnswers.length === 0) return

        commitAndNavigate(currentQuestion.id, currentAnswers)
    }

    // single choice: the selection itself moves to the next question
    const handleSelectChoice = (choiceId: string) => {
        if (!currentQuestion?.id) return
        if (isNavigatingRef.current) return

        commitAndNavigate(currentQuestion.id, [choiceId])
    }

    // skips an optional question: its answer is erased and the "ignorée" trigger is followed
    const handleSkip = () => {
        if (!currentQuestion?.id || !currentQuestion.optional) return
        if (isNavigatingRef.current) return

        commitAndNavigate(currentQuestion.id, [])
    }

    /**
     * Automatically navigates through questions marked as "auto".
     * These questions do not require any user interaction.
     */
    useEffect(() => {
        if (!currentQuestionId) return
        const question = workflowQuestions.find((q) => q.id === currentQuestionId)
        if (!question?.auto) return

        setVisitedQuestionIds((previous) =>
            previous.includes(currentQuestionId) ? previous : [...previous, currentQuestionId],
        )

        let isCancelled = false

        const advanceAutoNode = async () => {
            const updatedMap = await processAutoNode(currentQuestionId, committedAnswersByQuestionId)
            if (isCancelled) return
            setCommittedAnswersByQuestionId(updatedMap)

            const nextId = resolveAutoNextQuestionId(currentQuestionId, workflowQuestions, workflowJsonContent)
            if (nextId) {
                enterQuestion(nextId, 'forward', updatedMap)
                setCurrentQuestionId(nextId)
                setVisitedQuestionIds((previous) => (previous.includes(nextId) ? previous : [...previous, nextId]))
                if (transmissionId) {
                    persistCurrentQuestion(transmissionId, nextId)
                }
            }
        }
        advanceAutoNode()
        return () => {
            isCancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestionId])

    const handleBack = async () => {
        if (!canGoBack || !previousQuestionId || isNavigatingRef.current) return
        startNavigation()

        enterQuestion(previousQuestionId, 'back', committedAnswersByQuestionId)
        setCurrentQuestionId(previousQuestionId)
        if (transmissionId) {
            persistCurrentQuestion(transmissionId, previousQuestionId)
        }
        try {
            await saveProgress(committedAnswersByQuestionId, previousQuestionId)
        } catch (error) {
            console.error('Erreur de sauvegarde:', error)
        } finally {
            endNavigation()
        }
    }

    const updateRecapAnswer = (questionId: string, value: any) => {
        const question = workflowQuestions.find((item) => item.id === questionId)
        const isArrayAnswer = question?.inputType === 'pj' || Boolean(question?.multiple)
        const nextAnswer = value == null || value === '' ? [] : isArrayAnswer && Array.isArray(value) ? value : [value]
        setCommittedAnswersByQuestionId((previous) => {
            const next = new Map(previous)
            next.set(questionId, nextAnswer)
            return next
        })
        // keep local answers in sync so re-opening the question from "back" reflects the recap edit
        setLocalAnswersByQuestionId((previous) => {
            const next = new Map(previous)
            next.set(questionId, nextAnswer)
            return next
        })
    }

    const handleRecapTransmission = () => {
        sendTransmission(committedAnswersByQuestionId)
    }

    // Clear the cache when the transfer is complete
    useEffect(() => {
        if (transmissionId && transmissionDetail?.status === 'Completed') {
            clearPersistedProgress(transmissionId)
        }
    }, [transmissionId, transmissionDetail?.status])

    return {
        currentQuestion,
        currentQuestionId,
        isRecap,
        visitedQuestionIds,
        updateRecapAnswer,
        handleRecapTransmission,
        canTransmitRecap: !recapHasErrors,
        progress,
        committedAnswersByQuestionId,
        localAnswersByQuestionId,
        setLocalAnswersByQuestionId,
        handleNext,
        handleSelectChoice,
        handleSkip,
        handleBack,
        showBack,
        canGoBack,
        isNavigating,
    }
}
